import { CommonModule } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { cache } from '@sama/rxjs-core';
import { isNull, isUndefined } from 'lodash';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { isTextObjectState } from '../../../../../cross-team-shared/annotation/shared/state/object-state';
import { BrowserControlService } from '../../../../../cross-team-shared/misc-services/browser-control.service';
import { skipDefaultErrorMessagingToken } from '../../../../../cross-team-shared/misc-services/resources/url-request.service';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { ObjectSelectionService } from '../../../../outputs/annotation/annotation-shared/utils/object-selection.service';

@Component({
  selector: 'h3-read-only-text-cell',
  templateUrl: './read-only-text-cell.component.html',
  styleUrls: ['./read-only-text-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class ReadOnlyTextCellComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @HostBinding('class.is-selected') isSelected!: boolean | undefined;

  @Input({ required: true }) itemId!: number;
  @Input() contextIndex?: number | null;
  private readonly store = inject(TaskWorkspaceStore);
  private readonly browserControlService = inject(BrowserControlService);
  private readonly objectSelectionService = inject(ObjectSelectionService);

  private readonly httpClient = inject(HttpClient);

  protected contextValue$!: Observable<string>;
  protected isImage$!: Observable<boolean>;
  protected isUrl$!: Observable<boolean>;
  protected isSelected$!: Observable<boolean | undefined>;

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (event[this.browserControlService.getPlatformModifier()]) {
      this.objectSelectionService.toggleSelection(this.itemId);
    } else {
      this.objectSelectionService.setSelection([this.itemId]);
      this.store('categorizationAnnotationPanel')('isOpen').set(true);
    }
  }

  ngOnInit() {
    this.setIsSelected();
    this.setContextValue();
  }

  private setIsSelected() {
    this.isSelected$ = this.store('objectSelection')(this.itemId).$;

    this.bindToInstance('isSelected', this.isSelected$);
  }

  private setContextValue() {
    if (isNull(this.contextIndex) || isUndefined(this.contextIndex)) {
      this.contextValue$ = of('');
      return;
    }

    this.contextValue$ = this.store.getObjectStore(this.contextIndex).$.pipe(
      map((context) => {
        assert(isTextObjectState(context), 'Context must be a TextObject');
        return context.tags.text;
      }),
      cache(),
    );

    this.isImage$ = this.contextValue$.pipe(
      switchMap((text) =>
        isValidUrl(text) ? this.isImageUrl$(text) : of(false),
      ),
    );

    this.isUrl$ = this.contextValue$.pipe(
      switchMap((text) => (isValidUrl(text) ? of(true) : of(false))),
    );
  }

  private isImageUrl$(url: string) {
    return this.httpClient
      .head(url, {
        observe: 'response',
        context: new HttpContext().set(skipDefaultErrorMessagingToken, true),
      })
      .pipe(
        map((response) => {
          const contentType = response.headers.get('Content-Type');
          return !!contentType?.startsWith('image/');
        }),
        catchError(() => of(false)),
      );
  }
}

function isValidUrl(possibleUrl: string) {
  try {
    new URL(possibleUrl);
    return true;
  } catch (_) {
    return false;
  }
}
